/**
 * FormatLockPill — Feb 28, 2026 (v2: per-section)
 *
 * Click the pill → small popover with 6 checkboxes (HPI / ROS / PE /
 * A/P / EDU / MDM). State lives in localStorage as a JSON map keyed by
 * section slug. The pill label reflects current state at a glance:
 *
 *   - "Lock OFF"       — no sections locked
 *   - "Lock A/P"       — exactly one section locked, named
 *   - "Lock (3)"       — multiple sections locked, count shown
 *   - "Lock ALL"       — all 6 sections locked
 *
 * Send shape on regenerate: useGeneration reads localStorage and posts
 * `format_locks: {hpi: bool, ros: bool, pe: bool, ap: bool, edu: bool,
 * mdm: bool}`. The backend prepends a section-scoped FORMAT LOCK
 * directive that bans bullet-reorder / re-indent / merge-split / header
 * rewrite inside the locked sections only — unlocked sections may still
 * be improved normally.
 *
 * Backwards compatibility: when only A/P is locked, the legacy
 * `format_lock_ap: true` flag is ALSO sent so older backends keep
 * working until they redeploy.
 */
import React, { useEffect, useState } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { persistProfilePref } from '../../utils/persistProfilePref';

const KEY = 'writeright_format_locks';
const LEGACY_KEY = 'writeright_format_lock_ap';

const SECTIONS = [
  { id: 'hpi', label: 'HPI',  hint: 'History of Present Illness' },
  { id: 'ros', label: 'ROS',  hint: 'Review of Systems' },
  { id: 'pe',  label: 'PE',   hint: 'Physical Exam' },
  { id: 'ap',  label: 'A/P',  hint: 'Assessment & Plan' },
  { id: 'edu', label: 'EDU',  hint: 'Patient Education' },
  { id: 'mdm', label: 'MDM',  hint: 'Medical Decision Making' },
];

const readLocks = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    }
    // Fall back to legacy single-A/P flag so users upgrading don't lose
    // their existing lock.
    if (localStorage.getItem(LEGACY_KEY) === 'true') {
      return { ap: true };
    }
  } catch { /* ignore */ }
  return {};
};

const writeLocks = (locks) => {
  try { localStorage.setItem(KEY, JSON.stringify(locks)); }
  catch { /* ignore */ }
  // Mirror the A/P flag to the legacy key so the older payload field
  // (`format_lock_ap`) stays in sync. Backend honors whichever it sees.
  try { localStorage.setItem(LEGACY_KEY, locks.ap ? 'true' : 'false'); }
  catch { /* ignore */ }
  // Feb 28, 2026 — server-persist so per-section locks follow the user
  // across devices (was localStorage-only previously).
  try { persistProfilePref({ format_locks: locks }); } catch { /* ignore */ }
};

const countOn = (locks) =>
  SECTIONS.reduce((n, s) => n + (locks[s.id] ? 1 : 0), 0);

const labelFor = (locks) => {
  const n = countOn(locks);
  if (n === 0) return 'Lock';
  if (n === SECTIONS.length) return 'Lock ALL';
  if (n === 1) {
    const only = SECTIONS.find((s) => locks[s.id]);
    return `Lock ${only?.label || ''}`;
  }
  return `Lock (${n})`;
};

export const FormatLockPill = ({ disabled = false }) => {
  const [locks, setLocks] = useState(readLocks);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const sync = () => setLocks(readLocks());
    window.addEventListener('storage', sync);
    window.addEventListener('writeright:format-lock-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('writeright:format-lock-changed', sync);
    };
  }, []);

  const toggleSection = (id) => {
    const next = { ...locks, [id]: !locks[id] };
    setLocks(next);
    writeLocks(next);
    try {
      window.dispatchEvent(new CustomEvent('writeright:format-lock-changed', {
        detail: { locks: next },
      }));
    } catch { /* ignore */ }
  };

  const setAll = (val) => {
    const next = SECTIONS.reduce((acc, s) => ({ ...acc, [s.id]: val }), {});
    setLocks(next);
    writeLocks(next);
    try {
      window.dispatchEvent(new CustomEvent('writeright:format-lock-changed', {
        detail: { locks: next },
      }));
    } catch { /* ignore */ }
    toast.success(val ? 'All sections locked' : 'All sections unlocked', { duration: 1500 });
  };

  const anyOn = countOn(locks) > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          title={
            anyOn
              ? `${countOn(locks)} section${countOn(locks) === 1 ? '' : 's'} locked. Click to manage.`
              : 'Click to lock specific sections (HPI/ROS/PE/A/P/EDU/MDM) on regenerate.'
          }
          aria-pressed={anyOn}
          data-testid="format-lock-pill"
          data-state={anyOn ? 'on' : 'off'}
          data-count={countOn(locks)}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-medium leading-tight transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
            anyOn
              ? 'bg-amber-500/20 border-amber-400/60 text-amber-200 ring-1 ring-amber-400/40'
              : 'bg-secondary/40 border-border text-muted-foreground hover:bg-amber-500/10 hover:border-amber-500/40 hover:text-amber-300'
          }`}
        >
          {anyOn ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
          <span>{labelFor(locks)}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-60 p-3 bg-popover border border-amber-500/30"
        data-testid="format-lock-popover"
      >
        <div className="mb-2 pb-2 border-b border-border/60">
          <div className="text-[11px] font-bold text-amber-300 uppercase tracking-wider flex items-center gap-1.5">
            <Lock className="w-3 h-3" />
            Format Lock
          </div>
          <div className="text-[10px] text-muted-foreground mt-0.5 leading-snug">
            On Regenerate, locked sections keep their bullets / indentation /
            order EXACTLY. Unlocked sections still improve normally.
          </div>
        </div>
        <div className="space-y-1.5">
          {SECTIONS.map((s) => {
            const on = !!locks[s.id];
            return (
              <label
                key={s.id}
                className={`flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer transition-colors ${
                  on ? 'bg-amber-500/15' : 'hover:bg-secondary/40'
                }`}
                data-testid={`format-lock-section-${s.id}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleSection(s.id)}
                  className="accent-amber-400 w-3.5 h-3.5"
                  data-testid={`format-lock-checkbox-${s.id}`}
                />
                <span className={`text-[11px] font-bold ${on ? 'text-amber-200' : 'text-foreground'}`}>
                  {s.label}
                </span>
                <span className="text-[10px] text-muted-foreground ml-auto">{s.hint}</span>
              </label>
            );
          })}
        </div>
        <div className="flex gap-1.5 mt-3 pt-2 border-t border-border/40">
          <button
            type="button"
            onClick={() => setAll(true)}
            className="flex-1 text-[10px] px-2 py-0.5 rounded border border-amber-500/40 text-amber-300 hover:bg-amber-500/15 transition-colors"
            data-testid="format-lock-all-btn"
          >
            Lock all
          </button>
          <button
            type="button"
            onClick={() => setAll(false)}
            className="flex-1 text-[10px] px-2 py-0.5 rounded border border-border text-muted-foreground hover:bg-secondary/60 transition-colors"
            data-testid="format-lock-none-btn"
          >
            Unlock all
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FormatLockPill;
